// frontend/src/EvaluatorProcessList.tsx - Restored and updated with new progress summary panel

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';
import ModerationIndicator from './ModerationIndicator';

// --- UPDATED INTERFACE TO MATCH BACKEND ---
interface ProcessSummary {
  id: number;
  name: string;
  description: string | null;
  ito_reference: string | null;
  score_scale: string;
  status: string;
  total_suppliers: number;
  total_compliant_suppliers: number;
  non_compliant_suppliers: number;
  evaluations_not_started: number;
  evaluations_in_progress: number;
  evaluations_completed: number;
  overall_status: string;
}

interface EvaluatorProcessListProps {
  evaluatorId: number;
  resumeOnly?: boolean;
  onProcessSelect: (processId: number, processName: string) => void;
  onBack: () => void;
}

const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)'
    },
    header: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: 'var(--spacing-md) var(--spacing-lg)',
      borderBottom: '1px solid var(--color-surface-variant)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky' as 'sticky',
      top: 0,
      zIndex: 50,
      boxShadow: 'var(--shadow-md)'
    },
    logoContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
    },
    logo: {
        height: 'var(--logo-size-lg)',
        width: 'auto'
    },
    card: {
      backgroundColor: 'var(--color-surface)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--color-surface-variant)',
      marginBottom: 'var(--spacing-lg)'
    },
};

const EvaluatorProcessList: React.FC<EvaluatorProcessListProps> = ({
  evaluatorId,
  resumeOnly = false,
  onProcessSelect,
  onBack
}) => {
  const [processes, setProcesses] = useState<ProcessSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    active: true,
    completed: true
  });

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  useEffect(() => {
    const loadProcesss = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = Cookies.get('access_token');
        const response = await axios.get(
          `${API_URL}/api/evaluations/evaluator/${evaluatorId}/processes`,
          { 
            params: { resume_only: resumeOnly },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setProcesses(response.data);
        
      } catch (error: any) {
        console.error('Error loading evaluator processes:', error);
        setError(error.response?.data?.detail || 'Failed to load processes');
      } finally {
        setIsLoading(false);
      }
    };
    loadProcesss();
  }, [evaluatorId, resumeOnly]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return '#10b981';
      case 'In Progress': return '#f59e0b';
      case 'Not Started': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return '✅';
      case 'In Progress': return '🔄';
      case 'Not Started': return '⏸️';
      default: return '❓';
    }
  };
  
  const getProgressPercentage = (process: ProcessSummary) => {
  if (process.total_compliant_suppliers === 0) return 0;
  // Progress based on compliant suppliers only
  return Math.round((process.evaluations_completed / process.total_compliant_suppliers) * 100);
};

    if (isLoading) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto', padding: '3rem'}}>
                <div className="loading-spinner" style={{ margin: '0 auto 1rem auto' }}></div>
                <div style={{ fontSize: '1.2rem', color: '#374151' }}>Loading Your Processes...</div>
            </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
       <div style={styles.mainContainer} className="density-compact">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto', padding: '3rem', color: '#dc2626'}}>
                <p>Error: {error}</p>
                <button onClick={onBack} className="btn btn-secondary">← Back to Dashboard</button>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer} className="density-compact">
      <main style={{ padding: '2rem' }}>
        <div className="text-center mb-xl">
            <h2 className="page-title">{resumeOnly ? 'Resume Evaluation' : 'Start Evaluation'}</h2>
            <p className="page-subtitle">{resumeOnly ? 'Select a process to continue your work' : 'Select a process to begin your evaluation'}</p>
        </div>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {processes.length === 0 ? (
            <div style={styles.card}>
              <div style={{textAlign: 'center', padding: '2rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: 'bold'}}>{resumeOnly ? 'No In-Progress Evaluations' : 'No Processes Assigned'}</h3>
                <p style={{color: '#6b7280'}}>{resumeOnly ? 'You have completed all your assigned evaluations.' : 'Contact your administrator if you believe this is an error.'}</p>
              </div>
            </div>
          ) : (
            <>
              {(() => {
                // Group processs into Active and Completed
                const activeProcesses = processes.filter(process => 
                  process.overall_status === 'In Progress' || process.overall_status === 'Not Started'
                );
                const completedProcesses = processes.filter(process => 
                  process.overall_status === 'Completed'
                );

                return (
                  <>
                    {/* Active Processs Group */}
                    {activeProcesses.length > 0 && (
                      <div style={styles.card}>
                        <div 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            marginBottom: expandedGroups.active ? '1.5rem' : '0'
                          }}
                          onClick={() => toggleGroup('active')}
                        >
                          <span style={{ 
                            fontSize: '1.2rem', 
                            marginRight: '0.5rem',
                            transition: 'transform 0.2s ease',
                            transform: expandedGroups.active ? 'rotate(90deg)' : 'rotate(0deg)'
                          }}>
                            ▶
                          </span>
                          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                            🔄 Active Processes ({activeProcesses.length})
                          </h2>
                        </div>
                        
                        {expandedGroups.active && (
                          <div style={{ display: 'grid', gap: '1.5rem' }}>
                            {activeProcesses.map(process => {
                  const progressPercentage = getProgressPercentage(process);
                  return (
                    <div
                      key={process.id}
                      className="card"
                      style={{ 
                        cursor: 'pointer', 
                        transition: 'all 0.2s ease',
                        background: 'var(--color-surface-variant)',
                        padding: 'var(--spacing-lg)'
                      }}
                      onClick={() => onProcessSelect(process.id, process.name)}
                       onMouseEnter={(e) => { 
                         e.currentTarget.style.borderColor = 'var(--color-primary)'; 
                         e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; 
                       }}
                       onMouseLeave={(e) => { 
                         e.currentTarget.style.borderColor = 'var(--color-surface-variant)'; 
                         e.currentTarget.style.boxShadow = 'var(--shadow-md)'; 
                       }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <div>
                          <h3 style={{ margin: 0, marginBottom: '0.5rem', color: 'var(--color-text-primary)' }}>{process.name}</h3>
                          <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{process.ito_reference}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <ModerationIndicator 
                            processId={process.id} 
                            size="small" 
                            showText={false}
                          />
                          <span className="badge" style={{ 
                            background: getStatusColor(process.overall_status), 
                            color: 'white'
                          }}>
                            {getStatusIcon(process.overall_status)} {process.overall_status}
                          </span>
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Evaluation Progress</span>
                          <span style={{ fontSize: '0.9rem' }}>
                            {process.evaluations_completed}/{process.total_compliant_suppliers} compliant suppliers evaluated ({progressPercentage}%)
                          </span>
                        </div>
                        <div className="progress">
                          <div 
                            className="progress-bar" 
                            style={{ 
                              background: progressPercentage === 100 ? 'var(--color-success)' : 'var(--color-primary)',
                              width: `${progressPercentage}%`
                            }}
                          ></div>
                        </div>
                      </div>

                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(4, 1fr)', 
                        gap: '1rem', 
                        fontSize: '0.9rem', 
                        borderTop: '1px solid var(--color-surface-variant)', 
                        paddingTop: '1rem' 
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-success)' }}>
                            {process.evaluations_completed}
                          </div>
                          <div style={{color: 'var(--color-text-secondary)'}}>Completed</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-warning)' }}>
                            {process.evaluations_in_progress}
                          </div>
                          <div style={{color: 'var(--color-text-secondary)'}}>In Progress</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-text-disabled)' }}>
                            {process.evaluations_not_started}
                          </div>
                          <div style={{color: 'var(--color-text-secondary)'}}>Not Started</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-error)' }}>
                            {process.non_compliant_suppliers}
                          </div>
                          <div style={{color: 'var(--color-text-secondary)'}}>Non-Compliant</div>
                        </div>
                      </div>
                    </div>
                  );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Completed Processs Group */}
                    {completedProcesses.length > 0 && (
                      <div style={styles.card}>
                        <div 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            marginBottom: expandedGroups.completed ? '1.5rem' : '0'
                          }}
                          onClick={() => toggleGroup('completed')}
                        >
                          <span style={{ 
                            fontSize: '1.2rem', 
                            marginRight: '0.5rem',
                            transition: 'transform 0.2s ease',
                            transform: expandedGroups.completed ? 'rotate(90deg)' : 'rotate(0deg)'
                          }}>
                            ▶
                          </span>
                          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                            ✅ Completed Processes ({completedProcesses.length})
                          </h2>
                        </div>
                        
                        {expandedGroups.completed && (
                          <div style={{ display: 'grid', gap: '1.5rem' }}>
                            {completedProcesses.map(process => {
                              const progressPercentage = getProgressPercentage(process);
                              return (
                                <div
                                  key={process.id}
                                  className="card"
                                  style={{ 
                                    cursor: 'pointer', 
                                    transition: 'all 0.2s ease',
                                    background: 'var(--color-surface-variant)',
                                    padding: 'var(--spacing-lg)'
                                  }}
                                  onClick={() => onProcessSelect(process.id, process.name)}
                                   onMouseEnter={(e) => { 
                                     e.currentTarget.style.borderColor = 'var(--color-primary)'; 
                                     e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; 
                                   }}
                                   onMouseLeave={(e) => { 
                                     e.currentTarget.style.borderColor = 'var(--color-surface-variant)'; 
                                     e.currentTarget.style.boxShadow = 'var(--shadow-md)'; 
                                   }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                    <div>
                                      <h3 style={{ margin: 0, marginBottom: '0.5rem', color: 'var(--color-text-primary)' }}>{process.name}</h3>
                                      <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{process.ito_reference}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <ModerationIndicator 
                                        processId={process.id} 
                                        size="small" 
                                        showText={false}
                                      />
                                      <span className="badge" style={{ 
                                        background: getStatusColor(process.overall_status), 
                                        color: 'white'
                                      }}>
                                        {getStatusIcon(process.overall_status)} {process.overall_status}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div style={{ marginBottom: '1rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                      <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Evaluation Progress</span>
                                      <span style={{ fontSize: '0.9rem' }}>
                                        {process.evaluations_completed}/{process.total_compliant_suppliers} compliant suppliers evaluated ({progressPercentage}%)
                                      </span>
                                    </div>
                                    <div className="progress">
                                      <div 
                                        className="progress-bar" 
                                        style={{ 
                                          background: progressPercentage === 100 ? 'var(--color-success)' : 'var(--color-primary)',
                                          width: `${progressPercentage}%`
                                        }}
                                      ></div>
                                    </div>
                                  </div>

                                  <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(4, 1fr)', 
                                    gap: '1rem', 
                                    fontSize: '0.9rem', 
                                    borderTop: '1px solid var(--color-surface-variant)', 
                                    paddingTop: '1rem' 
                                  }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-success)' }}>
                                        {process.evaluations_completed}
                                      </div>
                                      <div style={{color: 'var(--color-text-secondary)'}}>Completed</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-warning)' }}>
                                        {process.evaluations_in_progress}
                                      </div>
                                      <div style={{color: 'var(--color-text-secondary)'}}>In Progress</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-text-disabled)' }}>
                                        {process.evaluations_not_started}
                                      </div>
                                      <div style={{color: 'var(--color-text-secondary)'}}>Not Started</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-error)' }}>
                                        {process.non_compliant_suppliers}
                                      </div>
                                      <div style={{color: 'var(--color-text-secondary)'}}>Non-Compliant</div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
           <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '2rem' }}>
                <button onClick={onBack} className="btn btn-secondary">← Back to Dashboard</button>
            </div>
        </div>
      </main>
    </div>
  );
};

export default EvaluatorProcessList;